import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState, type MouseEvent } from 'react'
import { collaborateCountQuery, collaborateListQuery, collaborateTicketQuery, markCollaborateRead } from '../api/collaborate'
import { fromNow, stamp } from '../inventoree/format'
import './NotificationsBell.css'
import './CollaborateBell.css'

/**
 * CLOPOTELUL DE COLABORARE DIN ANTET - `#my_ticket_comments_btn` din
 * `_layout/header.php`, cu insigna `#collaborate-badge-header`.
 *
 * In portal, cifra o punea `resources/js/tasks/counters/Collaborate.js`
 * (`easeedesk/unread_comments_counter`, din minut in minut, cu `pulse-warning`
 * pe insigna), iar apasarea deschidea sertarul din dreapta pe fila
 * "Collaborate" (`resources/js/components/collaborate.js`): lista tichetelor
 * omului cu comentarii necitite (`easeedesk/myCommentsList`), fiecare cu
 * numarul de necitite, subiectul si ultimul comentariu; apasarea pe un tichet
 * arata necititele lui (`easeedesk/myTicketCommentsList/<id>`) si le marca
 * citite.
 *
 * Sertarul din dreapta nu s-a mutat inca (`SIDEBAR_READY` in `Shell.tsx`), asa
 * ca lista sta intr-un panou al clopotelului, pe tiparul clopotelului de
 * instiintari de langa el (`NotificationsBell.tsx`) - acelasi chenar, aceeasi
 * umbra. Markupul randurilor e cel din sabloanele sertarului
 * (`resources/templates/user_sidebar/collaborate/*.html`), pe fundal deschis.
 *
 * MARCAREA CA CITITE se face cand omul deschide necititele unui tichet, ca in
 * portal - dar printr-un POST al ei, nu din citire.
 */
export function CollaborateBell() {
  const [open, setOpen] = useState(false)
  const [ticket, setTicket] = useState<number | null>(null)
  const box = useRef<HTMLLIElement>(null)
  const navigate = useNavigate()
  const client = useQueryClient()

  const badge = useQuery(collaborateCountQuery)
  const list = useQuery({ ...collaborateListQuery, enabled: open && ticket === null })
  const one = useQuery({ ...collaborateTicketQuery(ticket ?? 0), enabled: open && ticket !== null })

  useEffect(() => {
    if (!open) return
    const away = (event: globalThis.MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('click', away)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('click', away)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  /* `TicketComments.fetchComments()`: dupa ce necititele s-au aratat, se
     marcheaza citite si cifra din antet se reciteste (`counters.runService`). */
  const shown = useRef<number | null>(null)
  useEffect(() => {
    if (ticket === null || !one.data || shown.current === ticket) return
    shown.current = ticket
    void markCollaborateRead(ticket)
      .then(() => client.invalidateQueries({ queryKey: collaborateCountQuery.queryKey }))
      .catch(() => undefined)
  }, [client, one.data, ticket])

  const count = badge.data?.count ?? 0
  const now = new Date()

  const openTicket = (id: number) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
    event.preventDefault()
    setOpen(false)
    void navigate({ to: `/easeedesk/ticket/${id}/comments-tab` })
  }

  return (
    <li className={`nav-quick-actions od-nav-bell od-nav-collab hidden-md hidden-sm hidden-xs${open ? ' open' : ''}`} ref={box}>
      <a
        id="my_ticket_comments_btn"
        className="dropdown-toggle"
        title="Collaboration notifications"
        href="#"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : 'false'}
        onClick={(e) => {
          e.preventDefault()
          setOpen((v) => !v)
          if (open) setTicket(null)
        }}
      >
        <i className="flaticon-comment flaticon-20" aria-hidden="true"></i>
        {/* `pulse-warning` cand e ceva de citit, `hidden` la zero - ca in Collaborate.js. */}
        <span id="collaborate-badge-header" className={`label bg-warning font-10${count > 0 ? ' pulse-warning' : ' hidden'}`}>
          {count}
        </span>
      </a>

      <div className="od-bell-panel" role="dialog" aria-label="Collaboration notifications" hidden={!open}>
        <div className="od-bell-head">
          <span className="od-bell-title">
            <i className="flaticon-comment" aria-hidden="true"></i> Collaborate
          </span>
          {ticket !== null && (
            <button
              type="button"
              className="od-bell-clear"
              onClick={() => {
                setTicket(null)
                shown.current = null
              }}
            >
              <i className="fa fa-angle-left" aria-hidden="true"></i> Back
            </button>
          )}
        </div>

        <div className="od-bell-body">
          {ticket === null ? (
            <>
              {list.isPending && <p className="od-bell-quiet">Reading…</p>}
              {list.isError && (
                <p className="od-bell-quiet od-bell-broken">
                  <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> The comments could not be read.
                </p>
              )}
              {list.data && list.data.tickets.length === 0 && (
                <p className="od-bell-quiet">
                  <i className="far fa-bell-slash" aria-hidden="true"></i> You don&apos;t have new messages
                </p>
              )}
              <ul className="collaborate-widget od-collab-list">
                {(list.data?.tickets ?? []).map((row) => {
                  const when = row.last ? stamp(row.last.created) : null
                  return (
                    <li
                      key={row.ticket}
                      className="comment od-collab-row"
                      onClick={() => {
                        shown.current = null
                        setTicket(row.ticket)
                      }}
                    >
                      <div className="od-collab-head">
                        <span className="badge bg-success pull-right od-collab-count">{row.count}</span>
                        <div className="pull-left">
                          <i className="fa fa-ticket-alt" aria-hidden="true"></i>{' '}
                          <a className="open-ticket-window" href={`/app/easeedesk/ticket/${row.ticket}/comments-tab`} onClick={openTicket(row.ticket)}>
                            <span className="od-collab-id"> #{row.ticket} </span>
                          </a>
                          <span>{row.subject}</span>
                        </div>
                      </div>
                      <div className="od-collab-last">
                        {when !== null && <span className="pull-right od-collab-when">{fromNow(when, now)}</span>}
                        {/* Ultimul comentariu e HTML din baza (`nl2br`), ca `{{{...}}}` din sablon. */}
                        {row.last && <small dangerouslySetInnerHTML={{ __html: row.last.comment }}></small>}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </>
          ) : (
            <>
              {one.isPending && <p className="od-bell-quiet">Reading…</p>}
              {one.isError && (
                <p className="od-bell-quiet od-bell-broken">
                  <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> The comments could not be read.
                </p>
              )}
              {one.data && (
                <>
                  <div className="od-collab-ticket">
                    <div className="pull-left od-collab-ticket-icon">
                      <i className="fa fa-ticket-alt" aria-hidden="true"></i>
                    </div>
                    <div className="pull-left">
                      <a className="open-ticket-window" href={`/app/easeedesk/ticket/${one.data.ticket}/comments-tab`} onClick={openTicket(one.data.ticket)}>
                        <span className="od-collab-id"> #{one.data.ticket} </span>
                      </a>
                      <span>{one.data.subject}</span>
                    </div>
                    <div className="clear"></div>
                  </div>
                  {one.data.comments.length === 0 && <p className="od-bell-quiet">No new comments on this ticket</p>}
                  {one.data.comments.map((comment) => {
                    const when = stamp(comment.created)
                    return (
                      <div className="od-collab-comment" key={comment.id}>
                        <div className="od-collab-comment-row">
                          <div className="od-collab-avatar">
                            <i className="fa fa-user-circle font-22" aria-hidden="true"></i>
                          </div>
                          <div className="od-collab-meta">
                            <span className="badge mini-badge" style={{ background: comment.typeColor || '#DDDDDD' }}>
                              {comment.typeName}
                            </span>{' '}
                            <span className="color-green3">{comment.author}</span> comment:
                            <span className="color-green3"> - {when === null ? comment.created : fromNow(when, now)}</span>
                          </div>
                        </div>
                        <div className="od-collab-body" dangerouslySetInnerHTML={{ __html: comment.comment }}></div>
                      </div>
                    )
                  })}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </li>
  )
}
